import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { logout } from "@/common/functions/authFunctions";
import { request } from "@/common/api";
import { useForm } from "react-hook-form";
import { Buttons } from "@/components/atoms/Buttons";
import { showAlert } from "@/components/containers/Alert";
import { TelControl } from "@/components/containers/Controls/TelControl";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { WithdrawInfo } from "@/components/atoms/WithdrawInfo";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { deleteFcmToken } from "@/common/functions/flutter";
import { useJoinFormHandlers } from "@/common/hooks/useJoinFormHandlers";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";

const schema = yup.object({
  withdraw: yup.object({
    userId: yup
      .string()
      .required("ID는 필수 입력입니다.")
      .matches(/^[a-zA-Z0-9]+$/, "ID는 영문 또는 숫자만 입력 가능합니다.")
      .min(4, "ID는 최소 4자 이상이어야 합니다.")
      .max(20, "ID는 최대 20자 이하이여야 합니다."),
    userIdCheck: yup
      .string()
      .required("ID는 필수 입력입니다.")
      .matches(/^[a-zA-Z0-9]+$/, "ID는 영문 또는 숫자만 입력 가능합니다.")
      .min(4, "ID는 최소 4자 이상이어야 합니다.")
      .max(20, "ID는 최대 20자 이하이여야 합니다."),
    tel: yup
      .string()
      .length(
        13,
        "연락처는 000-0000-0000 형식이며, 13자에 맞게 작성하셔야 합니다."
      )
      .required("연락처는 필수 입력입니다."),
  }),
});

export const SettingWithdrawPage = () => {
  /** 📦 State Store */
  const navigate = useNavigate();
  const { control } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });
  const {
    student,
    joinComm,
    handleIdChange,
    handleNameChange,
    handleNumberChange,
  } = useJoinFormHandlers();

  /** 📡 API Layer */
  const { mutateAsync: withdraw } = useMutation({
    mutationFn: (data: any) =>
      request({
        method: "POST",
        url: "/auth/withdrawal",
        data: data,
      }),
    onSuccess: async (data: any) => {
      if (data.success) {
        const confirmed = await showAlert(data.message);
        if (confirmed) {
          if (window.WebViewChannel) {
            deleteFcmToken();
          }
          logout();
          navigate("/");
        }
      } else {
        await showAlert(data.message);
      }
    },
  });

  /** 🚀 Action Handlers */
  const onSubmit = async () => {
    try {
      if (
        student.name.length > 0 &&
        student.tel.length > 0 &&
        joinComm.userId.length > 0
      ) {
        await withdraw({
          name: student.name,
          userId: joinComm.userId,
          tel: student.tel,
        });
      } else {
        showAlert("모든 항목을 입력해주세요.");
      }
    } catch (error) {
      console.error("@@@ error: ", error);
    }
  };

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          layout="text"
          color="bright100"
          icon={<IconArrow />}
          label="설정"
          onClick={() => window.history.back()}
        />
      }
      pageTitle="회원 탈퇴"
    >
      <Form
        submitBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="solid"
            label="회원 탈퇴"
            disabled={false}
            onClick={onSubmit}
          />
        }
      >
        <>
          <WithdrawInfo />
          <FormInputBox formTitle="탈퇴 요청 계정 확인">
            <>
              <TextControl
                control={control}
                name="student.name"
                label="이름"
                placeholder="이름을 입력하세요."
                required={true}
                value={student.name}
                onChange={(e) => handleNameChange(e, "student")}
              />

              <TextControl
                control={control}
                name="join.userId"
                label="아이디"
                placeholder="아이디를 입력하세요."
                required={true}
                value={joinComm.userId}
                onChange={handleIdChange}
              />
              <TelControl
                type="tel"
                control={control}
                name="student.tel"
                label="연락처"
                placeholder="000-0000-0000"
                required={true}
                value={student.tel}
                onChange={(e) => handleNumberChange(e, "student")}
              />
            </>
          </FormInputBox>
        </>
      </Form>
    </MainTemplate>
  );
};
